import { useLayoutEffect, useRef, type RefObject } from 'react';
import { Courier } from './courier/courier';
import { createDefaultBody } from './courier/defaultBody';
import type { CourierBody } from './courier/types';
import type { ObserverState } from './observerContext';

/*
  CoreCourier — слой курьера ядра в оболочке (spec-3-motion §5, §9): fixed inset-0, прозрачен для мыши, скрыт
  от читалки, z-fab — над холстом, меню и доком поиска, под ящиками, модалками, поповерами, тостами и
  подсказками. Рисует его провайдер в доме core; ход визита — в courier/courier.ts, тело — сменное
  (CourierBody, проп провайдера body).

  Слои: root — наш (translate по пути, WAAPI) → attach — наш (поправка на прокрутку и стену) → tilt и skin —
  тела. Рядом — contact (мениск, кольцо, контур строки) с той же поправкой; route — «Показать дорогу» на стенде.
  Обрезка по видимой части окна цели (clip) ставится только на время касания.
*/

interface CoreCourierProps {
  /** Сюда кладётся курьер: движок помощника зовёт его визиты. */
  courierRef: RefObject<Courier | null>;
  /** Сменное тело; без него — тело по умолчанию (капля, LiquidBody у цели). */
  body?: CourierBody;
  /** Состояние дома: тело по умолчанию берёт по нему форму у цели. */
  state: ObserverState;
}

export function CoreCourier({ courierRef, body, state }: CoreCourierProps) {
  const stateRef = useRef(state);
  const fallback = useRef<CourierBody | null>(null);
  const clip = useRef<HTMLDivElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const attach = useRef<HTMLDivElement>(null);
  const tilt = useRef<HTMLDivElement>(null);
  const skin = useRef<HTMLDivElement>(null);
  const contactWrap = useRef<HTMLDivElement>(null);
  const contactSvg = useRef<SVGSVGElement>(null);
  const film = useRef<HTMLDivElement>(null);
  const routeSvg = useRef<SVGSVGElement>(null);

  useLayoutEffect(() => {
    const courier = new Courier({
      clip: clip.current!,
      root: root.current!,
      attach: attach.current!,
      tilt: tilt.current!,
      skin: skin.current!,
      contactWrap: contactWrap.current!,
      contactSvg: contactSvg.current!,
      film: film.current!,
      routeSvg: routeSvg.current!,
    });
    courierRef.current = courier;
    return () => {
      courier.destroy();
      if (courierRef.current === courier) courierRef.current = null;
    };
  }, [courierRef]);

  useLayoutEffect(() => {
    stateRef.current = state;
  }, [state]);

  useLayoutEffect(() => {
    fallback.current ??= createDefaultBody({ state: () => stateRef.current });
    courierRef.current?.setBody(body ?? fallback.current);
  }, [courierRef, body]);

  return (
    <div aria-hidden data-slot="courier" className="pointer-events-none fixed inset-0 z-fab">
      <svg ref={routeSvg} className="absolute inset-0 size-full overflow-visible" />
      {/* Тон ядра (courier-tone, data-tone) — на общем слое: его берут и капля, и плёнка касания. */}
      <div ref={clip} className="absolute inset-0 courier-tone">
        <div ref={contactWrap} className="absolute inset-0">
          <svg ref={contactSvg} className="absolute inset-0 size-full overflow-visible" />
          <div ref={film} className="absolute inset-0" />
        </div>
        <div ref={root} className="absolute top-0 left-0">
          <div ref={attach}>
            <div ref={tilt}>
              <div ref={skin} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
